"use client";

import { Gift, Zap, CreditCard, Star, ArrowRight } from "lucide-react";
import { TrackedNavButton } from "@/components/ui/tracked-nav-button";
import { useButtonTracking } from "@/lib/useButtonTracking";
import { scrollToPricing } from "@/lib/utils";
import Image from "next/image";
import { Button } from "../ui/button";
import { useRouter } from "next/navigation";

interface AIBuilderPackProps {
  isMobile: boolean;
}


export default function AIBuilderPack({ isMobile }: AIBuilderPackProps) {
  const { trackButtonClick } = useButtonTracking();
  const router = useRouter();

  const handleGetAccess = () => {
    // Track the click for analytics
    trackButtonClick('ai_builder_pack_join', {
      section: 'ai_builder_pack',
      action: 'click',
      button_name: 'join_community'
    });
    
    // Redirect to sign in page instead of scrolling to pricing
    router.push('/auth/signin');
  };

  return (
    <section id="ai-builder-pack" className="section-container bg-black text-white pt-16">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h2 className={`${isMobile ? 'text-5xl' : 'text-6xl'} font-bold mb-6`}>
          AI Builder Pack
        </h2>

        <p className={`${isMobile ? 'text-xl' : 'text-2xl'} text-white/80 max-w-4xl mb-8`}>
          Join our community and get exclusive access to premium AI tools and credits
        </p>

        <div className="bg-[#111111] border border-gray-800 p-4 sm:p-6 md:p-8 overflow-hidden rounded-3xl">
          <div className="flex items-center gap-2 mb-4 md:mb-6">
            <Star className="h-5 w-5 md:h-6 md:w-6 text-yellow-500 fill-yellow-500" />
            <span className="text-yellow-500 text-sm md:text-base font-medium">
              EXCLUSIVE
            </span>
          </div>

          {/* Perks Grid - More responsive */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
            <div className="bg-black rounded-lg p-3 md:p-4 flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all duration-300">
              <Image
                src="/1_hero/logos/replicate.png"
                alt="Replicate"
                width={80}
                height={32}
                className="h-6 md:h-8 w-auto mb-2 md:mb-3 group-hover:scale-105 transition-transform duration-300"
              />
              <p className="font-mono text-xs md:text-sm group-hover:text-white/90 transition-colors duration-300">$100 free credits</p>
            </div>

            <div className="bg-black rounded-lg p-3 md:p-4 flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all duration-300">
              <Image
                src="/1_hero/logos/firecrawl.png"
                alt="Firecrawl"
                width={80}
                height={32}
                className="h-6 md:h-8 w-auto mb-2 md:mb-3 group-hover:scale-105 transition-transform duration-300"
              />
              <p className="font-mono text-xs md:text-sm group-hover:text-white/90 transition-colors duration-300">50,000 credits</p>
            </div>

            <div className="bg-black rounded-lg p-3 md:p-4 flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all duration-300">
              <Image
                src="/1_hero/logos/windsurf.png"
                alt="Windsurf"
                width={80}
                height={32}
                className="h-6 md:h-8 w-auto mb-2 md:mb-3 group-hover:scale-105 transition-transform duration-300"
              />
              <p className="font-mono text-xs md:text-sm group-hover:text-white/90 transition-colors duration-300">3-Month Free</p>
            </div>

            <div className="bg-black rounded-lg p-3 md:p-4 flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all duration-300">
              <Image
                src="/1_hero/logos/llamaindex.png"
                alt="LlamaIndex"
                width={80}
                height={32}
                className="h-6 md:h-8 w-auto mb-2 md:mb-3 group-hover:scale-105 transition-transform duration-300"
              />
              <p className="font-mono text-xs md:text-sm group-hover:text-white/90 transition-colors duration-300">Llama cloud index</p>
            </div>

            <div className="bg-black rounded-lg p-3 md:p-4 flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all duration-300">
              <div className="flex items-center justify-center mb-2 md:mb-3 group-hover:scale-105 transition-transform duration-300">
                <div className="h-5 w-5 md:h-6 md:w-6 bg-white rounded-full flex items-center justify-center mr-1">
                  <span className="text-black text-xs md:text-sm font-bold">
                    B
                  </span>
                </div>
                <span className="font-bold text-xs md:text-sm">
                  BLACKBOX AI
                </span>
              </div>
              <p className="font-mono text-xs md:text-sm group-hover:text-white/90 transition-colors duration-300">$500 LLM credits</p>
            </div>

            <div className="bg-black rounded-lg p-3 md:p-4 flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all duration-300">
              <div className="h-10 w-10 md:h-14 md:w-14 bg-white/10 rounded-full flex items-center justify-center mb-2 md:mb-3 group-hover:scale-105 transition-transform duration-300">
                <span className="text-white text-2xl md:text-4xl font-medium">
                  ?
                </span>
              </div>
              <p className="font-mono text-xs md:text-sm group-hover:text-white/90 transition-colors duration-300">More...</p>
            </div>
          </div>

          <div className="flex flex-col mt-6 mb-4 md:mt-8 md:mb-8">
            <p className="text-white/80 text-lg md:text-xl mb-2 md:mb-4">
              Exclusive perks worth over $5,000
            </p>
            <p className="text-white/70 text-sm md:text-base mb-4 md:mb-6">
              Get free credits and special deals on top AI tools when you join
              our community
            </p>

            <Button
              className="bg-white text-black hover:bg-white/90 py-3 px-8 rounded-full font-medium transition-colors mt-2 w-fit inline-flex items-center group text-base"
              onClick={handleGetAccess}
            >
              <span>Join Community</span>
              <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
